'use client'

// MODULE AN1-P3 — PostHog (web). PRIVACY IS THE SPEC.
//
// - EU host only. Env-keyed: NEXT_PUBLIC_POSTHOG_KEY (+ optional NEXT_PUBLIC_POSTHOG_HOST,
//   default https://eu.i.posthog.com). Key ABSENT → ships DARK (no init, no network).
// - Session recording: HARD OFF (disable_session_recording). Autocapture ON for pageviews +
//   clicks only. Respects Do-Not-Track.
// - Identity: PSEUDONYMOUS distinct_id = the user's UUID — NEVER email/phone/name. Anonymous
//   until identified; person profiles for identified users only.
// - Content exclusion: PostHog never captures input/textarea VALUES in autocapture; on top of
//   that, compose surfaces carry the `ph-no-capture` class (dua compose, thread reply,
//   announcement editor) so no click/element event is captured there, and sanitize_properties
//   strips any obviously-PII property value defensively.

import { useEffect } from 'react'

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const PHONE_RE = /^\+?\d[\d\s\-()]{7,}$/

function scrub(value: unknown): unknown {
  if (typeof value === 'string') {
    if (EMAIL_RE.test(value.trim()) || PHONE_RE.test(value.trim())) return '<redacted>'
    // Redact one-time tokens from any captured URL ($current_url, $pathname, $referrer, …):
    // /verify/<token> and /reset-password/<token> are credential-equivalent.
    if (/\/(verify|reset-password)\//.test(value)) {
      return value.replace(/\/(verify|reset-password)\/[^/?#\s]+/g, '/$1/[redacted]')
    }
  }
  return value
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!key) return // DARK — no key, no analytics, no network call.
    // The private /verify surface is EXCLUDED from analytics entirely — do not init PostHog there, so
    // the one-time token never leaves the device (it's a standalone link, never navigated to in-app).
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/verify/')) return
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com'

    let cancelled = false
    ;(async () => {
      const posthog = (await import('posthog-js')).default
      if (cancelled) return
      posthog.init(key, {
        api_host: host,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: true, // HARD OFF — never record sessions.
        respect_dnt: true,
        person_profiles: 'identified_only', // pseudonymous; profile only after uuid identify
        // Defensive: drop any property value that looks like an email/phone before it leaves.
        sanitize_properties: (props: Record<string, unknown>) => {
          const out: Record<string, unknown> = {}
          for (const [k, v] of Object.entries(props)) out[k] = scrub(v)
          return out
        },
      })

      // Identify with the UUID only (never PII). Reads the session's user id from /api/auth/me.
      try {
        const res = await fetch('/api/auth/me', { headers: { Accept: 'application/json' } })
        if (res.ok) {
          const data = await res.json().catch(() => null)
          const uid = data?.user?.id
          if (uid && typeof uid === 'string') posthog.identify(uid) // no email/name/phone properties
          else posthog.reset() // logged out / guest → stay anonymous
        }
      } catch {
        // ignore — anonymous autocapture still works
      }
    })()

    return () => { cancelled = true }
  }, [])

  return <>{children}</>
}
